import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import i18n from "./i18n";
import { autoDetectLanguage } from "./i18n/geo";

import { AuthProvider } from "./context/AuthContext";
import { DataProvider } from "./context/DataContext";

import PreviewLayout from "./pages/preview/PreviewLayout";

import PreviewHome from "./pages/preview/PreviewHome";
import PreviewCabs from "./pages/preview/PreviewCabs";
import PreviewPackages from "./pages/preview/PreviewPackages";
import PreviewTransfers from "./pages/preview/PreviewTransfers";
import PreviewDestinations from "./pages/preview/PreviewDestinations";
import DestinationDetail from "./pages/preview/DestinationDetail";

import CabBooking from "./pages/CabBooking";
import PackageDetail from "./pages/PackageDetail";
import PackageBooking from "./pages/PackageBooking";
import TransferBooking from "./pages/TransferBooking";
import BookingConfirmation from "./pages/BookingConfirmation";
import MyBookings from "./pages/MyBookings";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Contact from "./pages/Contact";
import About from "./pages/About";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import RefundPolicy from "./pages/RefundPolicy";

import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Overview from "./pages/admin/Overview";
import ManageCabs from "./pages/admin/ManageCabs";
import ManagePackages from "./pages/admin/ManagePackages";
import ManageTransfers from "./pages/admin/ManageTransfers";
import ManageDestinations from "./pages/admin/ManageDestinations";
import ManagePopularDestinations from "./pages/admin/ManagePopularDestinations";
import ManageReviews from "./pages/admin/ManageReviews";
import Analytics from "./pages/admin/Analytics";
import HeroImages from "./pages/admin/HeroImages";
import DestinationEditor from "./pages/admin/DestinationEditor";
import ManageBookings from "./pages/admin/ManageBookings";
import Inquiries from "./pages/admin/Inquiries";
import PricingControl from "./pages/admin/PricingControl";
import SiteSettings from "./pages/admin/SiteSettings";

function App() {
    useEffect(() => {
        autoDetectLanguage(i18n);
    }, []);
    return (
        <div className="App font-body">
            <AuthProvider>
                <DataProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route element={<PreviewLayout />}>
                                {/* ===== PRIMARY SEO ROUTES ===== */}
                                <Route path="/" element={<PreviewHome />} />
                                <Route path="/cab-booking" element={<PreviewCabs />} />
                                <Route path="/cab-booking/book/:cabId" element={<CabBooking />} />
                                <Route path="/airport-transfers" element={<PreviewTransfers />} />
                                <Route path="/airport-transfers/book/:transferId" element={<TransferBooking />} />
                                <Route path="/tour-packages" element={<PreviewPackages />} />
                                <Route path="/tour-packages/:pkgId" element={<PackageDetail />} />
                                <Route path="/tour-packages/book/:pkgId" element={<PackageBooking />} />
                                <Route path="/destinations" element={<PreviewDestinations />} />
                                <Route path="/destinations/:slug" element={<DestinationDetail />} />
                                <Route path="/contact" element={<Contact />} />

                                {/* ===== Auxiliary public routes ===== */}
                                <Route path="/booking/confirmation/:bookingId" element={<BookingConfirmation />} />
                                <Route path="/my-bookings" element={<MyBookings />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                                <Route path="/terms" element={<TermsOfService />} />
                                <Route path="/refund-policy" element={<RefundPolicy />} />

                                {/* ===== Legacy short URLs → SEO-friendly URLs (301-style) ===== */}
                                <Route path="/cabs" element={<Navigate to="/cab-booking" replace />} />
                                <Route path="/cabs/book/:cabId" element={<RedirectWithParams from="/cabs/book/" to="/cab-booking/book/" />} />
                                <Route path="/packages" element={<Navigate to="/tour-packages" replace />} />
                                <Route path="/packages/:pkgId" element={<RedirectWithParams from="/packages/" to="/tour-packages/" />} />
                                <Route path="/packages/book/:pkgId" element={<RedirectWithParams from="/packages/book/" to="/tour-packages/book/" />} />
                                <Route path="/transfers" element={<Navigate to="/airport-transfers" replace />} />
                                <Route path="/transfers/book/:transferId" element={<RedirectWithParams from="/transfers/book/" to="/airport-transfers/book/" />} />
                            </Route>

                            {/* ===== ADMIN ===== */}
                            <Route path="/admin/login" element={<AdminLogin />} />
                            <Route path="/admin" element={<AdminLayout />}>
                                <Route index element={<Overview />} />
                                <Route path="cabs" element={<ManageCabs />} />
                                <Route path="packages" element={<ManagePackages />} />
                                <Route path="transfers" element={<ManageTransfers />} />
                                <Route path="destinations" element={<ManageDestinations />} />
                                <Route path="destinations/:slug/edit" element={<DestinationEditor />} />
                                <Route path="popular-destinations" element={<ManagePopularDestinations />} />
                                <Route path="reviews" element={<ManageReviews />} />
                                <Route path="analytics" element={<Analytics />} />
                                <Route path="hero-images" element={<HeroImages />} />
                                <Route path="bookings" element={<ManageBookings />} />
                                <Route path="inquiries" element={<Inquiries />} />
                                <Route path="pricing" element={<PricingControl />} />
                                <Route path="settings" element={<SiteSettings />} />
                            </Route>

                            {/* ===== Legacy preview routes ===== */}
                            <Route path="/preview" element={<Navigate to="/" replace />} />
                            <Route path="/preview/cabs" element={<Navigate to="/cab-booking" replace />} />
                            <Route path="/preview/packages" element={<Navigate to="/tour-packages" replace />} />
                            <Route path="/preview/transfers" element={<Navigate to="/airport-transfers" replace />} />

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </BrowserRouter>
                    <Toaster
                        position="top-center"
                        richColors
                        toastOptions={{
                            classNames: { toast: "font-body" },
                        }}
                    />
                </DataProvider>
            </AuthProvider>
        </div>
    );
}

/* Tiny helper: rewrites `/old/:id` → `/new/:id` while preserving search params */
const RedirectWithParams = ({ from, to }) => {
    const path = window.location.pathname.replace(from, to);
    const search = window.location.search || "";
    return <Navigate to={`${path}${search}`} replace />;
};

export default App;
